export type ExaminationStatus = "ok" | "soon" | "expired" | "missing";

export interface ExaminationStatusDetail {
  valid_until: string | null;
  status: ExaminationStatus;
  days_remaining: number | null;
}

export interface EmployeeExamination {
  id: string;
  employee: string;
  bhp_valid_until: string | null;
  medical_valid_until: string | null;
  bhp_status: ExaminationStatusDetail;
  medical_status: ExaminationStatusDetail;
  notes: string;
  updated_at: string;
}

export interface EmployeeDocumentType {
  id: string;
  name: string;
  is_active: boolean;
  sort_order: number;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export const EMPLOYEE_DOCUMENT_LOCATIONS = [
  { value: "office_wroclaw", label: "Biuro Wrocław" },
  { value: "office_opole",   label: "Biuro Opole" },
  { value: "archive_opole",  label: "Archiwum Opole" },
] as const;

export type EmployeeDocumentLocationValue =
  (typeof EMPLOYEE_DOCUMENT_LOCATIONS)[number]["value"];

export interface EmployeeDocument {
  id: string;
  employee: string;
  document_type: string;
  document_type_name: string;
  location: string;
  location_display: string;
  title: string;
  date_signed: string | null;
  effective_from: string | null;
  effective_to: string | null;
  file: string | null;
  file_detail: { id: string; url: string; original_filename: string } | null;
  file_url: string | null;
  notes: string;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface DocumentActivityEntry {
  id: string;
  actor_name: string;
  verb: string;
  description: string;
  timestamp: string;
  metadata: Record<string, unknown>;
}

// ── Electronic signatures ───────────────────────────────────────────────────

export const ESIGNATURE_PROVIDERS = [
  { value: "szafir",     label: "Szafir (KIR)" },
  { value: "sigillum",   label: "Sigillum (PWPW)" },
  { value: "certum",     label: "Certum (Asseco)" },
  { value: "eurocert",   label: "EuroCert" },
  { value: "simplysign", label: "SimplySign" },
  { value: "mobywatel",  label: "mObywatel" },
  { value: "other",      label: "Inny" },
] as const;

export type ESignatureProvider = (typeof ESIGNATURE_PROVIDERS)[number]["value"];

export const ESIGNATURE_TYPES = [
  { value: "qualified", label: "Kwalifikowany" },
  { value: "advanced",  label: "Zaawansowany" },
  { value: "simple",    label: "Zwykły" },
] as const;

export type ESignatureType = (typeof ESIGNATURE_TYPES)[number]["value"];

export const ESIGNATURE_MEDIA = [
  { value: "card",      label: "Karta kryptograficzna" },
  { value: "usb_token", label: "Token USB" },
  { value: "cloud",     label: "Chmura" },
  { value: "mobywatel", label: "mObywatel" },
  { value: "other",     label: "Inny" },
] as const;

export type ESignatureMedium = (typeof ESIGNATURE_MEDIA)[number]["value"] | "";

export interface EmployeeESignature {
  id: string;
  employee: string;
  provider: ESignatureProvider;
  provider_display: string;
  signature_type: ESignatureType;
  signature_type_display: string;
  medium: ESignatureMedium;
  medium_display: string;
  serial_number: string;
  issued_at: string | null;
  valid_until: string | null;
  notes: string;
  status: ExaminationStatusDetail;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export type EmployeeESignatureWrite = Partial<
  Pick<
    EmployeeESignature,
    "provider" | "signature_type" | "medium" | "serial_number" | "issued_at" | "valid_until" | "notes"
  >
>;
